import { DialogActions, DialogContent, Grid, Select, MenuItem, FormControl, Button } from '@mui/material';
import { DialogCustom, DialogTitleCustom, FetchAutocomplete, FieldWrap } from '@tap/mui-ui';
import { useState } from 'react';
import { MdEditor } from '@tap/mui-ui';
import { DocumentCode, documentService, IApiDocument, relationLinkService } from '@tap/data-providers';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ICancelDialogResult, IDialogProps } from '@tap/tap-ui';

/**
 *  Диалог создания связанной ссылки
 */

type IRelationLinkCreateDialogProps = IDialogProps<{ documentKey: string }, ICancelDialogResult>;

export type ICreateRelationLinkProps = {
    relationTypeCode: string,
    relatedDocument: IApiDocument | null,
    relatedDocumentKey: string,
}


const relationTypeList = [
    { name: 'Относится к', id: 1, code: 'related_to' },
];

const errorMessageByDocumentType = {
    [DocumentCode.MES]: 'Вы не можете связывать заказ с самим собой',
    [DocumentCode.SALE]: 'Вы не можете связывать заказ с самим собой',
    [DocumentCode.WO]: 'Вы не можете связывать заказ с самим собой',
    [DocumentCode.DSG]: 'Вы не можете связывать задание с самим собой',
    [DocumentCode.UGM]: 'Вы не можете связывать заявку с самой собой',
    [DocumentCode.QC]: 'Вы не можете связывать заявку с самой собой',
    [DocumentCode.TOOL]: 'Вы не можете связывать заявку с самой собой',
    [DocumentCode.WIP]: 'Вы не можете связывать заявку с самой собой',
    [DocumentCode.TECH]: 'Вы не можете связывать заявку с самой собой',
    [DocumentCode.PLAN]: 'Вы не можете связывать план с самим собой',
    [DocumentCode.USER]: 'Вы не можете связывать пользователя с самим собой',
};

export const RelationLinkCreateDialog = ({
    data,
    isOpen,
    handleClose,
}: IRelationLinkCreateDialogProps) => {

    const [newComment, setNewComment] = useState<any>('');


    const handleNewCommentOnChange = ({ text }: any) => {
        setNewComment(text);
    }

    const handleCancel = () => {
        handleClose({ isCancel: true });
    }

    const documentCode = data.documentKey.split('-')[0];

    const { values, touched, errors, ...formik } = useFormik<ICreateRelationLinkProps>({
        validateOnMount: true,
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        initialValues: {
            relationTypeCode: relationTypeList[0].code,
            relatedDocument: null,
            relatedDocumentKey: '',
        },
        validationSchema: Yup.object({
            relationTypeCode: Yup.string()
                .required('Выберите тип связи.'),
            relatedDocument: Yup.object()
                .required('Выберите документ.'),
            relatedDocumentKey: Yup.string()
                .required('Выберите документ.')
                .notOneOf([data.documentKey], errorMessageByDocumentType[documentCode] || 'Вы не можете связывать заявку с самой собой'),
        }),

        onSubmit: (values) => {
            return relationLinkService.createRelationLink(data.documentKey, values.relationTypeCode, values.relatedDocumentKey)
                .then(() => {
                    handleClose({ isCancel: false });
                });
        },
    });

    return (
        <DialogCustom
            handleClose={handleCancel}
            open={isOpen}
        >

            <DialogTitleCustom
                primaryText={'Связать запись'}
            />

            <DialogContent>
                <Grid
                    item
                    container
                    spacing={2}
                    alignItems='stretch'
                >

                    <FieldWrap label={'Эта запись'}>
                        <Select
                            autoFocus
                            value={values.relationTypeCode}
                            size='small'
                            sx={{
                                maxWidth: '300px',
                                width: '100%',
                            }}
                            name="relationType"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={touched.relationTypeCode && Boolean(errors.relationTypeCode)}
                            inputProps={{
                                helpertext: touched.relationTypeCode && errors.relationTypeCode,
                            }}
                        >
                            {relationTypeList.map(rt => <MenuItem key={rt.id} value={rt.code}>{rt.name}</MenuItem>)}
                        </Select>
                    </FieldWrap>

                    <FieldWrap label={'Запись'}>
                        <FetchAutocomplete
                            sx={{
                                width: '100%',
                            }}
                            fetchRequest={({ searchText }) => {
                                return documentService.getDocumentList({ searchText }).then((res) => res.rows);
                            }}
                            value={values.relatedDocument}
                            onChange={(e, value) => {
                                formik.setValues({
                                    ...values,
                                    relatedDocument: value,
                                    relatedDocumentKey: value?.documentKey || '',
                                }, true).then(() => {
                                    formik.setFieldTouched('relatedDocument');
                                });
                            }}
                            textFieldProps={{
                                name: 'relatedDocument',
                                //onBlur: formik.handleBlur,
                                error: touched.relatedDocument && Boolean(errors.relatedDocumentKey),
                                helperText: touched.relatedDocument && errors.relatedDocumentKey,
                            }}
                            getOptionLabel={(option) => `${option.documentKey}: ${option.title}`}
                            noOptionsText={'Запись не найдена'}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                    </FieldWrap>

                    <FieldWrap label={'Комментарий'}>
                        <FormControl sx={{ width: '100%' }}>
                            <MdEditor
                                onChange={handleNewCommentOnChange}
                                value={newComment}
                            />
                        </FormControl>
                    </FieldWrap>

                </Grid>
            </DialogContent>

            <DialogActions>
                <Grid container justifyContent='flex-end'>

                    <LoadingButton
                        loading={formik.isSubmitting}
                        disableElevation
                        type='button'
                        variant='contained'
                        color='primary'
                        sx={{ mr: 2 }}
                        onClick={formik.submitForm}
                        disabled={!formik.isValid}
                    >
                        Связать
                    </LoadingButton>

                    <Button
                        disableElevation
                        type='button'
                        variant='text'
                        color='primary'
                        onClick={handleCancel}
                    >
                        Отмена
                    </Button>

                </Grid>
            </DialogActions>

        </DialogCustom >
    );
}
